// This file is auto-generated by "@aria-ui/cli". Do not edit this file directly.

import { registerMenuRootElement, type MenuRootElement, type MenuRootProps as MenuRootElementProps, type MenuRootEvents } from '@prosekit/web/menu';
import { createElement, forwardRef, useCallback, useRef, type ForwardedRef, type ForwardRefExoticComponent, type HTMLAttributes, type RefAttributes, useLayoutEffect } from 'react';

/** Props for the {@link MenuRoot} React component. */
export interface MenuRootProps {
  /**
   * Whether the overlay is initially open.
   * @default false
   */
  defaultOpen?: MenuRootElementProps['defaultOpen'];
  /**
   * Whether the overlay is currently open.
   * @default null
   */
  open?: MenuRootElementProps['open'];
  /**
   * Whether the component should ignore user interaction.
   * @default false
   */
  disabled?: MenuRootElementProps['disabled'];
  /** Emitted when the menu is opened or closed. */
  onOpenChange?: (event: MenuRootEvents['openChange']) => void;
}

function MenuRootComponent(props: MenuRootProps, forwardedRef: ForwardedRef<MenuRootElement>) {
  registerMenuRootElement();

  const elementRef = useRef<MenuRootElement>(null);
  const handlersRef = useRef<Array<((event: Event) => void) | undefined>>([]);

  const { defaultOpen: p0, disabled: p1, open: p2, onOpenChange: e0, ...restProps } = props;

  useLayoutEffect(() => {
    const element = elementRef.current as Record<string, unknown> | null;
    if (!element) return;
    Object.assign(element, { defaultOpen: p0, disabled: p1, open: p2 });
    handlersRef.current = [e0] as Array<((event: Event) => void) | undefined>;
  });

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const ac = new AbortController();
    for (const [index, eventName] of ['openChange'].entries()) {
      element.addEventListener(
        eventName,
        (event: Event) => {
          handlersRef.current[index]?.(event);
        },
        { signal: ac.signal },
      );
    }
    return () => ac.abort();
  }, []);

  const mergedRef = useCallback(
    (element: MenuRootElement | null) => {
      elementRef.current = element;
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    },
    [forwardedRef],
  );

  return createElement('prosekit-menu-root', { ...restProps, ref: mergedRef, suppressHydrationWarning: true });
}

/** A React component that renders an `prosekit-menu-root` custom element. */
export const MenuRoot: ForwardRefExoticComponent<MenuRootProps & HTMLAttributes<MenuRootElement> & RefAttributes<MenuRootElement>> = /* @__PURE__ */ forwardRef(MenuRootComponent);
